import { stateWord } from "@lafayette/domain";
import type { StaffRequestPriorityValue, StaffRequestStateValue } from "@lafayette/schemas";
import type { ReactNode } from "react";
import { Badge } from "../../ui/badge";
import { Icon } from "../../ui/icon";
import { StatusBadge, type StatusTone } from "../../ui/status-badge";
import { type Look, lookOf } from "../../ui/status-look";
import { priorityLabels, priorityLook, stateLook } from "../labels";

// The small pieces every request screen shares (and the payment and itinerary screens too). A badge always carries a word, never a colour
// alone. Everything here is built from the kit (`src/ui`) and semantic tokens only.

/** The class of an inline link in running text or in a cell. */
export const linkClass = "text-link underline underline-offset-4 hover:no-underline";

/** A plain list without bullets (the kit has no list part). */
export const listClass = "m-0 grid list-none gap-3 p-0";

/** Fields side by side while each has room (at least 11 rem), one under the other when it does not. */
export const fieldGridClass = "grid grid-cols-[repeat(auto-fit,minmax(11rem,1fr))] gap-3";

/** A line of help or small print under a block. */
export const hintClass = "m-0 text-sm leading-6 text-muted-foreground";

/** A line that says there is nothing yet. */
export const emptyNoteClass = "m-0 text-sm text-muted-foreground";

/** The pill of a list row: 24 px high, 12.5 px semibold. The kit badge is a little smaller. */
const pillClass = "h-6 py-0 text-[12.5px] font-semibold";

/** The dot that a state pill starts with: a small filled circle in the tone. The word beside it is the status. */
const dot = <Icon name="circle" className="size-1.5! fill-current" />;

/** A `Look` as a list pill: its own glyph and its word. */
export function LookPill({ look, children }: Readonly<{ look: Look; children: ReactNode }>) {
  return (
    <StatusBadge tone={look.tone} glyph={<Icon name={look.icon} />} className={pillClass}>
      {children}
    </StatusBadge>
  );
}

/** A pill that starts with a dot of its tone, then the word. Used for the state of a request and of a payment. */
export function DotPill({ tone, children }: Readonly<{ tone: StatusTone; children: ReactNode }>) {
  return (
    <StatusBadge tone={tone} glyph={dot} className={pillClass}>
      {children}
    </StatusBadge>
  );
}

/** The state of a request. A state that this build does not know reads as the neutral word with the neutral look, never as a crash. */
export function StateBadge({ state }: Readonly<{ state: StaffRequestStateValue | (string & {}) }>) {
  return (
    <DotPill tone={lookOf(stateLook, state).tone}>
      {stateWord("request", "staffShort", state)}
    </DotPill>
  );
}

export function PriorityBadge({ priority }: Readonly<{ priority: StaffRequestPriorityValue }>) {
  return (
    <Badge tone={priorityLook[priority].tone} className={pillClass}>
      {priority === "STANDARD" ? priorityLabels.STANDARD : `${priorityLabels[priority]} priority`}
    </Badge>
  );
}

/** The note of a block that the customer reads word for word. */
export function CustomerReadsNote({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <p className="m-0 flex items-start gap-2 text-sm leading-6 text-success">
      <Icon name="view" className="mt-1 size-4 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

/** A heading inside a panel (below the `h2` of the panel). */
export function SubHeading({ children }: Readonly<{ children: ReactNode }>) {
  return <h3 className="m-0 text-base leading-6 font-semibold">{children}</h3>;
}
